import { useEffect, useState } from 'react'
import { ensureSettings, seedDatabase } from '@/db/seed'
import { useAuthStore } from '@/store/useAuthStore'
import {
  initConnectivityListeners,
  initThemeListener,
  useDataStore,
  useNotificationStore,
  useSettingsStore,
} from '@/store'
import { env } from '@/lib/env'
import { describeError } from '@/services/api'
import { syncPushSubscription } from '@/lib/push'

export type BootstrapState = 'starting' | 'ready' | 'failed'

/**
 * One-time application boot: open/seed IndexedDB, load settings, hydrate the
 * stores, and start the connectivity/theme listeners.
 */
export function useBootstrap(): { state: BootstrapState; error: string | null } {
  const [state, setState] = useState<BootstrapState>('starting')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const teardown: Array<() => void> = []

    async function boot() {
      try {
        // Establish who (if anyone) is signed in before loading any data —
        // an anonymous visitor must not trigger a wave of 401s.
        await useAuthStore.getState().check()
        if (cancelled) return
        if (useAuthStore.getState().status !== 'authenticated') {
          // Signing in later re-runs this via the subscription below.
          setState('ready')
          return
        }

        if (env.seedDemoData) await seedDatabase()
        await ensureSettings()
        if (cancelled) return

        await useSettingsStore.getState().load()
        const currentPersonId = useSettingsStore.getState().settings?.currentPersonId ?? null
        useNotificationStore.getState().setPerson(currentPersonId)

        await Promise.all([
          useDataStore.getState().loadCore(),
          useNotificationStore.getState().load(),
        ])
        if (cancelled) return

        teardown.push(initConnectivityListeners(), initThemeListener())
        setState('ready')

        // Re-register a browser that is subscribed but unknown to the server
        // (a restored backup, replaced VAPID keys, a failed first attempt).
        // Deliberately not awaited: notifications must never delay startup.
        void syncPushSubscription()
      } catch (bootError) {
        if (cancelled) return
        setError(describeError(bootError))
        setState('failed')
      }
    }

    void boot()

    // Signing in mid-session must load the data the early return skipped,
    // otherwise the app comes up authenticated but completely empty.
    let lastStatus = useAuthStore.getState().status
    const unsubscribe = useAuthStore.subscribe((auth) => {
      if (auth.status === lastStatus) return
      const becameSignedIn = lastStatus !== 'authenticated' && auth.status === 'authenticated'
      lastStatus = auth.status
      if (becameSignedIn) void boot()
    })

    return () => {
      cancelled = true
      unsubscribe()
      teardown.forEach((fn) => fn())
    }
  }, [])

  // Keep the notification feed scoped to whoever is signed in.
  useEffect(
    () =>
      useSettingsStore.subscribe((settings) => {
        const personId = settings.settings?.currentPersonId ?? null
        const store = useNotificationStore.getState()
        if (store.personId !== personId) {
          store.setPerson(personId)
          void store.load()
        }
      }),
    [],
  )

  return { state, error }
}
